import React, { useState } from 'react';
import styled from 'styled-components';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import useApi from '../../hooks/useApi';

const PORTAL_USER_KEY = 'portal_user';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';

const Container = styled.section`
  max-width: 520px;
  margin: 4rem auto 2rem;
  background: rgba(15, 23, 42, 0.82);
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 1rem;
  padding: 2rem;
  color: #e2e8f0;
`;

const Title = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.8rem;
`;

const Description = styled.p`
  margin: 0 0 1.5rem;
  color: #94a3b8;
  font-size: 0.88rem;
  line-height: 1.45;
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  margin-bottom: 0.4rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.7rem 0.9rem;
  margin-bottom: 1rem;
  border-radius: 0.6rem;
  border: 1px solid rgba(148, 163, 184, 0.28);
  background: rgba(15, 23, 42, 0.5);
  color: #f8fafc;
`;

const PasswordFieldWrap = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const PasswordInput = styled(Input)`
  margin-bottom: 0;
  padding-right: 4.2rem;
`;

const TogglePasswordButton = styled.button`
  position: absolute;
  right: 0.45rem;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  color: #93c5fd;
  cursor: pointer;
  padding: 0.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const EyeIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M2 12C3.7 8.4 7.5 6 12 6C16.5 6 20.3 8.4 22 12C20.3 15.6 16.5 18 12 18C7.5 18 3.7 15.6 2 12Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {!open ? (
      <path
        d="M3 3L21 21"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ) : null}
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const Button = styled.button`
  width: 100%;
  border: 0;
  border-radius: 0.6rem;
  padding: 0.75rem 1rem;
  background: #2563eb;
  color: #f8fafc;
  font-weight: 700;
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};
`;

const Message = styled.p`
  margin-top: 0.9rem;
  color: ${(props) => (props.error ? '#fca5a5' : '#93c5fd')};
  min-height: 1.2rem;
`;

const PolicyList = styled.ul`
  margin: 0.3rem 0 1rem;
  padding-left: 1.1rem;
  color: #94a3b8;
  font-size: 0.86rem;
  line-height: 1.5;
`;

const resolvePortalHomePath = (roles = []) => {
  if (
    roles.includes('super_admin') ||
    roles.includes('admin') ||
    roles.includes('teacher') ||
    roles.includes('student') ||
    roles.includes('parent')
  ) {
    return '/portal/home';
  }
  return '/portal/login';
};

const resolveLoginRedirectPath = (search, roles = []) => {
  const next = (new URLSearchParams(search || '').get('next') || '').trim();
  const normalized = next.replace(/\/+$/, '');
  if (normalized === '/recreation' || normalized === '/portal/recreation') {
    return '/portal/recreation';
  }
  return resolvePortalHomePath(roles);
};

const readPortalUser = () => {
  try {
    const raw = sessionStorage.getItem(PORTAL_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.roles)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const PortalLoginPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { request, loading } = useApi();
  const [mode, setMode] = useState('login');
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [setupLoginId, setSetupLoginId] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState('');
  const portalUser = readPortalUser();
  const isRecreationLogin = resolveLoginRedirectPath(location.search) === '/portal/recreation';

  if (portalUser) {
    return <Redirect to={resolveLoginRedirectPath(location.search, portalUser.roles)} />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      const data = await request({
        endpoint: `${BACKEND_BASE_URL}/api/auth/login`,
        method: 'POST',
        body: {
          login_id: loginId.trim(),
          password,
        },
      });
      if (data?.status === 'password_setup_required') {
        setMode('setup');
        setSetupLoginId(data.login_id || loginId.trim());
        setPassword('');
        setMessage('첫 로그인입니다. 새 비밀번호를 설정해 주세요.');
        return;
      }
      if (!data?.user) {
        setMessage('로그인 응답 형식이 올바르지 않습니다.');
        return;
      }
      sessionStorage.setItem(PORTAL_USER_KEY, JSON.stringify(data.user));
      window.dispatchEvent(new Event('portal_user_updated'));
      if (data.user.must_change_password) {
        setMessage('초기 비밀번호입니다. 로그인 후 비밀번호를 변경해 주세요.');
      }
      history.replace(resolveLoginRedirectPath(location.search, data.user.roles || []));
    } catch (err) {
      setMessage(err?.responseData?.message || '로그인에 실패했습니다.');
    }
  };

  const handleSetupPassword = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      const data = await request({
        endpoint: `${BACKEND_BASE_URL}/api/auth/setup-password`,
        method: 'POST',
        body: {
          login_id: setupLoginId.trim(),
          new_password: newPassword,
          confirm_password: confirmPassword,
        },
      });
      if (!data?.user) {
        setMessage('비밀번호 설정 응답 형식이 올바르지 않습니다.');
        return;
      }
      sessionStorage.setItem(PORTAL_USER_KEY, JSON.stringify(data.user));
      window.dispatchEvent(new Event('portal_user_updated'));
      history.replace(resolveLoginRedirectPath(location.search, data.user.roles || []));
    } catch (err) {
      setMessage(err?.responseData?.message || '비밀번호 설정에 실패했습니다.');
    }
  };

  return (
    <Container>
      {mode === 'login' ? (
        <>
          <Title>{isRecreationLogin ? '레크레이션 로그인' : '포털 로그인'}</Title>
          <Description>
            {isRecreationLogin
              ? '포털 계정으로 로그인하면 레크레이션 페이지로 이동합니다.'
              : '최초 로그인은 초기 비밀번호 0000으로 로그인한 뒤 새 비밀번호를 설정하세요.'}
          </Description>
          <form onSubmit={handleSubmit}>
            <Label htmlFor="portal-login-id">아이디</Label>
            <Input
              id="portal-login-id"
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
              placeholder="아이디 또는 전화번호"
              autoComplete="username"
            />
            <Label htmlFor="portal-password">비밀번호</Label>
            <PasswordFieldWrap>
              <PasswordInput
                id="portal-password"
                type={showLoginPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호"
                autoComplete="current-password"
              />
              <TogglePasswordButton
                type="button"
                onClick={() => setShowLoginPassword((prev) => !prev)}
                aria-label={showLoginPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
              >
                <EyeIcon open={showLoginPassword} />
              </TogglePasswordButton>
            </PasswordFieldWrap>
            <Button type="submit" disabled={loading}>
              {loading ? '로그인 중...' : '로그인'}
            </Button>
          </form>
        </>
      ) : (
        <>
          <Title>초기 비밀번호 설정</Title>
          <Description>
            첫 로그인 보안을 위해 비밀번호를 새로 설정해 주세요.
          </Description>
          <form onSubmit={handleSetupPassword}>
            <Label htmlFor="portal-setup-login-id">아이디</Label>
            <Input
              id="portal-setup-login-id"
              value={setupLoginId}
              onChange={(e) => setSetupLoginId(e.target.value)}
              placeholder="아이디"
              autoComplete="username"
            />
            <Label htmlFor="portal-new-password">새 비밀번호</Label>
            <PasswordFieldWrap>
              <PasswordInput
                id="portal-new-password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="새 비밀번호"
                autoComplete="new-password"
              />
              <TogglePasswordButton
                type="button"
                onClick={() => setShowNewPassword((prev) => !prev)}
                aria-label={showNewPassword ? '새 비밀번호 숨기기' : '새 비밀번호 보기'}
              >
                <EyeIcon open={showNewPassword} />
              </TogglePasswordButton>
            </PasswordFieldWrap>
            <PolicyList>
              <li>8자 이상</li>
              <li>영문 대문자 1개 이상</li>
              <li>영문 소문자 1개 이상</li>
              <li>숫자 1개 이상</li>
              <li>특수문자 1개 이상 (`!@#$%^&*()_+-=[]{};:,.?/`)</li>
              <li>공백/한글/이모지 사용 불가</li>
            </PolicyList>
            <Label htmlFor="portal-confirm-password">비밀번호 확인</Label>
            <PasswordFieldWrap>
              <PasswordInput
                id="portal-confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="비밀번호 확인"
                autoComplete="new-password"
              />
              <TogglePasswordButton
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label={showConfirmPassword ? '비밀번호 확인 숨기기' : '비밀번호 확인 보기'}
              >
                <EyeIcon open={showConfirmPassword} />
              </TogglePasswordButton>
            </PasswordFieldWrap>
            <Button type="submit" disabled={loading}>
              {loading ? '설정 중...' : '비밀번호 설정'}
            </Button>
          </form>
        </>
      )}
      <Message error={Boolean(message) && /실패|오류|일치/.test(message)}>
        {message}
      </Message>
    </Container>
  );
};

export default PortalLoginPage;
